<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    loading-message="Loading Emails..."
    button-location="top"
    @submit="searchEmails()"
  >
    <template #title>
      Email Search
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search Type*</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchCondition.type"
            :options="queryTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Search Value*</label>
        <div class="col-sm-5">
          <sanitized-input
            id="query"
            v-model="searchCondition.query"
            placeholder="Please input search value"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Status</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="searchCondition.status"
            :options="statusTypeList"
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10"> Date Range </label>
        <div class="col-sm-5">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          >
            <template #comment>
              <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
            </template>
          </date-picker>
        </div>

        <div class="col-sm-3 col-12 form-submit-button-group left-button-group pt-5 mobile-pt-0">
          <p-button
            type="purple"
            round
            native-type="submit"
            data-test="btn-email-search"
            :disabled="status.isLoading"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </div>
      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="status.currentPage"
          :total-pages="status.totalPages"
          @load-page-data="searchEmails"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="emails"
        :table-columns="tableColumns"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, watch, computed } from 'vue';
import { orderBy } from 'lodash';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { Email } from '@/models/email';
import { ListType } from '@/models/app';
import { TableColumnType } from '@/models/table';
import { EmailsQueryVariables } from '@/gql/graphql';
import { emailService } from '@/services/email';

const pageId = 'search-email';
const paginationLimit = 100;
const queryTypeList: ListType[] = [
  { id: 1, key: 'email', label: 'Email' },
  { id: 2, key: 'messageId', label: 'Message ID' },
  { id: 3, key: 'userId', label: 'User ID' },
  { id: 4, key: 'id', label: 'ID' },
  { id: 5, key: 'rideId', label: 'Ride ID' },
  { id: 6, key: 'scheduledRideId', label: 'Scheduled Ride ID' },
];
const statusTypeList: ListType[] = [
  { id: 1, key: '', label: 'All Status' },
  { id: 2, key: 'bounce', label: 'Bounce' },
  { id: 3, key: 'click', label: 'Click' },
  { id: 4, key: 'deferred', label: 'Deferred' },
  { id: 5, key: 'delivered', label: 'Delivered' },
  { id: 6, key: 'dropped', label: 'Dropped' },
  { id: 7, key: 'open', label: 'Open' },
  { id: 8, key: 'processed', label: 'Processed' },
  { id: 9, key: 'requested', label: 'Requested' },
  { id: 10, key: 'spamreport', label: 'Spam Report' },
];
const tableColumns: TableColumnType[] = [
  { label: 'Created At', field: 'createdAt', width: 180, isDateField: true },
  { label: 'From', field: 'fromEmail', width: 200 },
  { label: 'To', field: 'toEmail', width: 200 },
  { label: 'Name', field: 'name', width: 200 },
  { label: 'Subject', field: 'subject', width: 250 },
  { label: 'Associated Object Id', field: 'associatedObjectId', width: 180 },
  { label: 'Status', field: 'status', width: 150, isCapitalize: true },
  { label: 'Purpose', field: 'purpose', width: 200 },
  { label: 'Reason', field: 'reason', minWidth: 250, isReadMore: true, maxLength: 100 },
  { label: 'Opened At', field: 'openedAt', width: 150, isCenterAlign: true },
];

const { customerInfo } = useCustomer();
const { showErrorPopupDialog } = useSwalDialog();

const searchCondition: Ref<EmailsQueryVariables> = ref({
  type: 'email',
  query: '',
  status: '',
  start: '',
  end: '',
});

const emails: Ref<Email[]> = ref([]);
const status = reactive({
  isLoading: false,
  totalPages: 1,
  currentPage: 1,
});
const timeSpanValue: Ref<any> = ref(null);

const customerEmail = computed(() => customerInfo.value?.email);

const searchEmails = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;
  status.currentPage = currentPage;
  status.isLoading = true;

  try {
    const response = await emailService.fetchEmails({
      query: searchCondition.value.query,
      type: searchCondition.value.type,
      status: searchCondition.value.status,
      start: searchCondition.value.start,
      end: searchCondition.value.end,
      pagination: {
        limit: paginationLimit,
        offset,
      },
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    emails.value = response.rows.map((item: Email) => ({
      ...item,
      associatedObjectId: item.rideId || item.scheduledRideId || item.groceryId || item.scheduledGroceryId || item.deliveryOrderId,
    }));

    emails.value = orderBy(emails.value, ['createdAt'], ['desc']);
  } catch (error) {
    emails.value = [];
    status.totalPages = 1;
    status.currentPage = 1;

    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const resetForm = () => {
  status.totalPages = 1;
  status.currentPage = 1;
  emails.value = [];
  searchCondition.value = {
    type: queryTypeList[0].key,
    query: '',
    status: '',
    start: '',
    end: '',
  };

  timeSpanValue.value = null;
};

watch(
  customerEmail,
  (value: string) => {
    resetForm();
    searchCondition.value.query = value;
    if (value) {
      searchEmails();
    }
  },
  {
    immediate: true,
  },
);

watch(
  () => searchCondition.value.type,
  () => {
    searchCondition.value.query = '';
  },
);

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchCondition.value.start = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchCondition.value.end = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchCondition.value.start = '';
    searchCondition.value.end = '';
  }
});

util.moveToTop();
</script>
